// WARNING: This function closely maps to a similar function in reader/utils.py on the backend.
// Any changes to this function should be reflected in the backend.

import { isElement } from '../../typeValidators';

/*
 * We classify elements into four categories:
 *   1. Grouping elements   – structural containers such as DIV, SECTION, ARTICLE …
 *   2. Block-text elements – paragraph-level text like P, H1-H6, LI …
 *   3. Media elements      – IMG, VIDEO, SVG … that don't hold visible text but are focusable.
 *   4. Inline elements     – SPAN, A, EM … (everything that is not any of the above).
 *
 * The traversal works depth-first:
 *   – First visit children so that we collect "deeper" focusable nodes before deciding whether the
 *     current element itself should be focusable.  This naturally gives child paragraphs priority
 *     over their parent blockquote, for example.
 *   – Visibility is checked through the inline style attribute for the cases covered by our tests
 *     (display:none, opacity:0 and width/height:0).
 *   – Grouping elements are only added when: (a) no focusable descendants were found, (b) the
 *     element contains readable text (either directly or inside inline descendants), and (c) every
 *     child element is inline-level (so we don't pick a DIV that wraps other block elements).
 */

const groupingTags = new Set([
  'DIV',
  'SECTION',
  'ARTICLE',
  'ASIDE',
  'BLOCKQUOTE',
  'FIGURE',
  'DETAILS',
  'FOOTER',
  'HEADER',
  'LEGEND',
  'MAIN',
  'NAV',
  'DL',
  'OL',
  'UL',
  'TABLE',
]);

const blockTextTags = new Set([
  'P',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'LI',
  'DD',
  'DT',
  'FIGCAPTION',
  'CAPTION',
  'TR',
]);

const mediaTags = new Set([
  'IMG',
  'VIDEO',
  'SVG',
  'AUDIO',
  'PICTURE',
  'OBJECT',
  'EMBED',
]);

// Pre-compile regex patterns for performance
const WIDTH_PATTERN = /width\s*:\s*0/;
const HEIGHT_PATTERN = /height\s*:\s*0/;
const MAX_HEIGHT_PATTERN = /max-height\s*:\s*0/;

function isMediaElement(el: Element): boolean {
  return mediaTags.has(el.nodeName);
}

function isBlockTextElement(el: Element): boolean {
  return blockTextTags.has(el.nodeName);
}

function isGroupingElement(el: Element): boolean {
  return groupingTags.has(el.nodeName);
}

function isInlineElement(el: Element): boolean {
  // Note: This checks semantic inline tags, not computed inline styling
  return !isGroupingElement(el) && !isBlockTextElement(el) && !isMediaElement(el);
}

function isHiddenBySize(styleAttr: string): boolean {
  return WIDTH_PATTERN.test(styleAttr) && HEIGHT_PATTERN.test(styleAttr) || MAX_HEIGHT_PATTERN.test(styleAttr);
}

function isVisible(el: Element): boolean {
  if (!(el instanceof HTMLElement)) {
    return true; // Non-HTMLElement nodes (e.g., SVGElement) – assume visible if present.
  }

  if ('checkVisibility' in el && typeof el.checkVisibility === 'function') {
    const isVisibleNative = el.checkVisibility({
      checkOpacity: true,
      checkVisibilityCSS: true,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error: contentVisibilityAuto is not yet in TypeScript's lib.dom.d.ts but is supported in modern browsers
      contentVisibilityAuto: true,
    });

    // checkVisibility returns true even for elements with width:0 and overflow:hidden
    // so we still need to check width/height manually
    if (!isVisibleNative) {
      return false;
    }

    const styleAttr = (el.getAttribute('style') || '').toLowerCase();

    // Use pre-compiled regex patterns for width/height checks
    if (isHiddenBySize(styleAttr)) {
      return false;
    }

    return true;
  }

  const styleAttr = (el.getAttribute('style') || '').toLowerCase();

  // Remove spaces for more reliable matching
  const styleNoSpaces = styleAttr.replace(/\s/g, '');
  if (styleNoSpaces.includes('display:none')) {
    return false;
  }
  // This covers content-visibility:hidden too
  if (styleNoSpaces.includes('visibility:hidden')) {
    return false;
  }
  if (styleNoSpaces.includes('opacity:0')) {
    return false;
  }

  // Use pre-compiled regex patterns
  if (isHiddenBySize(styleAttr)) {
    return false;
  }

  return true;
}

function hasNonEmptyText(el: Element, config: ContentElementsConfig = {}): boolean {
  if (!config.excludedTags?.size) {
    return (el.textContent || '').trim().length > 0;
  }

  const walker = document.createTreeWalker(
    el,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode: (node) => {
        const parent = node.parentElement;
        return parent && !config.excludedTags?.has(parent.nodeName)
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_REJECT;
      },
    },
  );

  let node = walker.nextNode();
  while (node) {
    if ((node.textContent || '').trim()) {
      return true;
    }
    node = walker.nextNode();
  }
  return false;
}

function containsOnlyInlineElements(el: Element, config: ContentElementsConfig = {}): boolean {
  for (const child of Array.from(el.children)) {
    if (config.excludedTags?.has(child.nodeName)) {
      continue;
    }
    if (!isInlineElement(child)) {
      return false;
    }
  }
  return true;
}

export interface ContentElementsConfig {
  shouldExcludeMedia?: boolean; // If true, excludes media elements
  excludedTags?: Set<string>; // Specific tags to exclude
}

function traverse(node: ChildNode, result: HTMLElement[], config: ContentElementsConfig = {}): void {
  if (!isElement(node)) {
    return;
  }

  const el = node;

  // Only process HTMLElements, skip SVG and other Element types
  if (!(el instanceof HTMLElement)) {
    return;
  }

  if (!isVisible(el)) {
    return;
  }

  if (config.excludedTags?.has(el.nodeName)) {
    return;
  }

  const initialLen = result.length;
  for (const child of Array.from(el.childNodes)) {
    traverse(child, result, config);
  }

  if (isMediaElement(el)) {
    if (!config.shouldExcludeMedia) {
      result.push(el);
    }
    return;
  }

  if (isBlockTextElement(el)) {
    // Check if any direct child is also a block text element
    const hasBlockChild = Array.from(el.children).some((child) =>
      child instanceof HTMLElement && isBlockTextElement(child));
    if (!hasBlockChild && hasNonEmptyText(el, config)) {
      result.push(el);
    }
    return;
  }

  if (isGroupingElement(el)) {
    if (result.length === initialLen && hasNonEmptyText(el, config) && containsOnlyInlineElements(el, config)) {
      result.push(el);
    }
  }
}

export function findContentElements(element: ChildNode, config: ContentElementsConfig = {}): HTMLElement[] {
  const result: HTMLElement[] = [];
  traverse(element, result, config);
  return result;
}
